import { ReactElement, useState } from 'react';
import { HiOutlineChevronRight } from 'react-icons/hi2';
import { SessionAnalysisType } from '../../../../types/SessionAnalysisType';
import {
  CalibrationSeverity,
  SEVERITY_CONFIG,
  getCalibrationBarSeverity,
  getConfigsWithBranch,
} from './calibrationSeverity';

const STATUS_SHOW_KEY = 'session-status-show';

// Short label shown after "Session Status –" for each severity.
const STATUS_LABEL: Record<CalibrationSeverity, string> = {
  [CalibrationSeverity.GRAY]: 'No checks run yet',
  [CalibrationSeverity.GREEN]: 'All good',
  [CalibrationSeverity.YELLOW]: 'Warning, expand for details',
  [CalibrationSeverity.RED]: 'Error, expand for details',
};

interface SessionStatusBarProps {
  session: any;
}

// Collapsible status box shown above the config list. For now it simply tracks
// the calibration bar's severity/colour, with one extra rule layered on top:
// a velodrome session with no velodrome route selected is always an error.
const SessionStatusBar = ({ session }: SessionStatusBarProps): ReactElement => {
  const [show, setShow] = useState<boolean>(() => {
    const stored = localStorage.getItem(STATUS_SHOW_KEY);
    return stored !== null ? JSON.parse(stored) : false;
  });

  const isVelodromeSession =
    session?.analysis_type === SessionAnalysisType.LOOP_VELODROME_INDOORS ||
    session?.analysis_type === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;
  // For velodrome sessions the selected velodrome is the session route.
  const velodromeMissing = isVelodromeSession && !session?.route_id;

  // Base severity tracks the calibration bar exactly.
  const calBar = getCalibrationBarSeverity(getConfigsWithBranch(session));

  // A velodrome session with no velodrome overrides everything else to an error.
  const severity = velodromeMissing ? CalibrationSeverity.RED : calBar.severity;
  const severityConfig = SEVERITY_CONFIG[severity];

  const label = velodromeMissing ? 'No velodrome selected' : STATUS_LABEL[severity];
  const detail = velodromeMissing
    ? 'This is a velodrome session but no velodrome has been selected. '
      + 'Choose a velodrome in the Calibration Parameters panel below.'
    : calBar.tooltip;

  const toggle = () => {
    setShow((prev) => {
      const next = !prev;
      localStorage.setItem(STATUS_SHOW_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div
      className="cr-mb-4 cr-rounded-lg cr-border-2 cr-border-solid cr-shadow-md cr-transition-colors cr-duration-300"
      style={{ backgroundColor: severityConfig.bg, borderColor: severityConfig.border }}
    >
      <div
        className="cr-flex cr-cursor-pointer cr-flex-row cr-items-center cr-gap-2 cr-px-5 cr-py-3"
        title={detail}
        onClick={toggle}
      >
        <HiOutlineChevronRight
          className={`cr-size-5 cr-stroke-[3px] cr-text-blue-900
                  cr-transition-transform cr-duration-[300ms] ${show && 'cr-rotate-90'}`}
        />
        <div
          className="cr-grow cr-text-center cr-font-main cr-text-base cr-font-semibold"
          style={{ color: '#03365D' }}
        >
          Session Status –{' '}
          <span className="cr-italic cr-font-normal" style={{ fontSize: '0.9em' }}>
            {label}
          </span>
        </div>
      </div>
      {show && (
        <div
          className="cr-rounded-b-lg cr-border-t-2 cr-border-solid cr-bg-white cr-px-5 cr-py-3"
          style={{ borderColor: severityConfig.border }}
        >
          <p className="cr-whitespace-pre-line cr-text-sm" style={{ color: '#03365D' }}>
            {detail}
          </p>
        </div>
      )}
    </div>
  );
};

export default SessionStatusBar;
